import {
  BottomSheet,
  Illustration,
  Text,
  TextButton,
  type UseModalState,
} from "@clipboard-health/ui-components";
import { Stack } from "@mui/material";
import { APP_V2_APP_EVENTS, useToast } from "@src/appV2/lib";
import { logEvent } from "@src/appV2/lib/analytics";
import { useLogEffect } from "@src/appV2/lib/analytics/useLogEffect";
import { DialogFooter } from "@src/appV2/redesign/components/DialogFooter";
import { IconButton } from "@src/appV2/redesign/components/IconButton";
import { LoadingButton } from "@src/appV2/redesign/components/LoadingButton";
import { capitalize } from "lodash";
import { Link } from "react-router-dom";

import { InterviewStatus } from "../../WorkerShifts/api/useFetchPaginatedInterviews";
import { useReportInterviewLateness } from "../api/useReportInterviewLateness";
import { useUpdateInterview } from "../api/useUpdateInterview";
import { type PlacementIncludedWorkplace } from "../types/fetchPlacements.schema";

interface PastInterviewCancelBottomSheetProps {
  isProspectWorkplace: boolean;
  modalState: UseModalState;
  interviewId: string;
  workplaceReliabilityBanRule: NonNullable<
    NonNullable<PlacementIncludedWorkplace["meta"]>["reliabilityBanRule"]
  >;
  startsInWorkplaceToday?: boolean;
  onCancelInterview: () => Promise<void>;
}

export function PastInterviewCancelBottomSheet(props: PastInterviewCancelBottomSheetProps) {
  const {
    isProspectWorkplace,
    modalState,
    interviewId,
    onCancelInterview,
    workplaceReliabilityBanRule,
    startsInWorkplaceToday,
  } = props;
  const { showErrorToast, showSuccessToast } = useToast();

  const cancelLabel = isProspectWorkplace ? "walk-in interview" : "interview";

  const eventContext = {
    interviewId,
    workplaceReliabilityBanRule,
    isProspectWorkplace,
  };
  useLogEffect(APP_V2_APP_EVENTS.PLACEMENT_PAST_INTERVIEW_CANCEL_MODAL_VIEWED, eventContext);

  const { mutateAsync: cancelInterview, isLoading: isCancelling } = useUpdateInterview({
    onSuccess: async () => {
      showSuccessToast(`${capitalize(cancelLabel)} cancelled`);
      modalState.closeModal();
      await onCancelInterview();
    },
    onError: () => {
      showErrorToast(`An error occurred cancelling ${cancelLabel}`);
    },
  });

  const { mutateAsync: reportLateness, isLoading: isReportingLateness } =
    useReportInterviewLateness({
      onSuccess: () => {
        showSuccessToast(
          "Thanks for letting us know! We gave the facility a heads up. They're looking forward to seeing you soon!"
        );
        modalState.closeModal();
      },
      onError: () => {
        showErrorToast(`An error occurred reporting lateness for ${cancelLabel}`);
      },
    });

  const handleCancel = async () => {
    logEvent(APP_V2_APP_EVENTS.PLACEMENT_PAST_INTERVIEW_CANCEL_MODAL_ACTIONS, {
      ...eventContext,
      action: "cancel",
    });

    await cancelInterview({
      interviewId,
      attributes: {
        status: InterviewStatus.CANCELLED,
      },
    });
  };

  const handleReportLateness = async () => {
    logEvent(APP_V2_APP_EVENTS.PLACEMENT_PAST_INTERVIEW_CANCEL_MODAL_ACTIONS, {
      ...eventContext,
      action: "report-lateness",
    });

    await reportLateness({
      interviewId,
    });
  };

  return (
    <BottomSheet
      modalState={modalState}
      footer={
        <DialogFooter orientation="vertical">
          {startsInWorkplaceToday ? (
            <>
              <LoadingButton
                fullWidth
                variant="contained"
                size="large"
                sx={{ mt: 1, mb: 3 }}
                isLoading={isReportingLateness}
                onClick={handleReportLateness}
              >
                I&apos;m just running late
              </LoadingButton>
              <TextButton
                LinkComponent={Link}
                size="large"
                disabled={isCancelling}
                onClick={handleCancel}
              >
                I need to cancel the interview
              </TextButton>
            </>
          ) : (
            <LoadingButton
              fullWidth
              variant="destructive"
              size="large"
              sx={{ mt: 1, mb: 3 }}
              isLoading={isCancelling}
              onClick={handleCancel}
            >
              Cancel
            </LoadingButton>
          )}
        </DialogFooter>
      }
    >
      <IconButton
        size="small"
        iconType="close"
        variant="outlined"
        sx={{ position: "absolute", top: 24, right: 16 }}
        onClick={modalState.closeModal}
      />
      <Stack gap={7} px={7} pb={8} pt="76px" alignItems="center" justifyContent="center">
        {startsInWorkplaceToday ? (
          <>
            <Illustration type="calendar-warning" />
            <Text variant="h2" textAlign="center">
              Your {cancelLabel} has started
            </Text>
            <Text textAlign="center" color={(theme) => theme.palette.text.secondary}>
              Please start heading to the facility now. If you cancel or no-show, you&apos;ll be
              restricted from viewing job openings for {workplaceReliabilityBanRule.restrictedDays}{" "}
              days.
            </Text>
          </>
        ) : (
          <>
            <Illustration type="cancellation" />
            <Text variant="h2" textAlign="center">
              Cancel your {cancelLabel}?
            </Text>
            <Text textAlign="center" color={(theme) => theme.palette.text.secondary}>
              You cancel your past interview, you&apos;ll be restricted from viewing job openings
              for {workplaceReliabilityBanRule.restrictedDays} days.
            </Text>
          </>
        )}
      </Stack>
    </BottomSheet>
  );
}
